import { useEffect, useRef, useState } from "react";
import type { FormEvent } from "react";
import GradientText from "../components/GradientText";
import Seo from "../components/Seo";

const neonColors = ["#ff43b8", "#2ed4ff", "#ff8a22", "#39d353", "#8b49ff"];

const subjectLabels: Record<string, string> = {
  catering: "Catering & Private Events",
  feedback: "Feedback",
  other: "Other",
};

export default function ContactPage() {
  const observerRef = useRef<IntersectionObserver | null>(null);
  const [selectedSubject, setSelectedSubject] = useState("catering");
  const [submitState, setSubmitState] = useState<
    "idle" | "submitting" | "success" | "error"
  >("idle");
  const [submitMessage, setSubmitMessage] = useState("");
  const isCateringInquiry = selectedSubject === "catering";

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" },
    );

    document
      .querySelectorAll(
        ".animate-fade-in-left, .animate-fade-in-right, .contact-method",
      )
      .forEach((el) => {
        observerRef.current?.observe(el);
      });

    return () => {
      observerRef.current?.disconnect();
    };
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const name = formData.get("name") as string;
    const email = formData.get("email") as string;
    const subject = formData.get("subject") as string;
    const message = formData.get("message") as string;
    const eventDate = formData.get("event_date") as string;
    const eventTime = formData.get("event_time") as string;
    const guestCount = formData.get("guest_count") as string;
    const phone = formData.get("phone") as string;
    const company = formData.get("company") as string;

    if (company) return;

    const serviceId = import.meta.env.VITE_EMAILJS_SERVICE_ID as
      | string
      | undefined;
    const templateId = import.meta.env.VITE_EMAILJS_TEMPLATE_ID as
      | string
      | undefined;
    const publicKey = import.meta.env.VITE_EMAILJS_PUBLIC_KEY as
      | string
      | undefined;

    if (!serviceId || !templateId || !publicKey) {
      console.error("Missing EmailJS environment variables.");
      setSubmitState("error");
      setSubmitMessage(
        "Email service is not configured. Set EmailJS environment variables.",
      );
      return;
    }

    const inquiryType = subjectLabels[subject] || "Inquiry";
    const subjectText = `Retro Grill - ${inquiryType}`;
    const submittedAt = new Date().toLocaleString("en-US", {
      dateStyle: "long",
      timeStyle: "short",
    });

    try {
      setSubmitState("submitting");
      setSubmitMessage("");
      const response = await fetch(
        "https://api.emailjs.com/api/v1.0/email/send",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            service_id: serviceId,
            template_id: templateId,
            user_id: publicKey,
            template_params: {
              name,
              email,
              message,
              phone,
              event_date: eventDate,
              event_time: eventTime,
              guest_count: guestCount,
              from_name: name,
              from_email: email,
              reply_to: email,
              inquiry_type: inquiryType,
              subject: subjectText,
              submitted_at: submittedAt,
            },
          }),
        },
      );

      if (!response.ok) {
        throw new Error(`EmailJS request failed: ${response.status}`);
      }

      form.reset();
      setSelectedSubject("catering");
      setSubmitState("success");
      setSubmitMessage("Thanks! Your message was sent.");
      setTimeout(() => {
        setSubmitState("idle");
        setSubmitMessage("");
      }, 4000);
    } catch (error) {
      console.error("Error sending contact form:", error);
      setSubmitState("error");
      setSubmitMessage(
        "Sorry, we couldn't send your message right now. Please try again.",
      );
    }
  };

  return (
    <>
      <Seo
        title="Contact"
        description="Contact Retro Grill for food truck bookings, catering requests, event questions, and menu inquiries."
        path="/contact"
        image="/images/RetroGrillLogo.webp"
      />
      {/* Page Header */}
      <section className="relative pt-[calc(80px+60px)] pb-[60px] px-6 text-center overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <div className="absolute inset-0 page-header-gradient-bg"></div>
          <div className="absolute inset-0 hero-grid-bg"></div>
        </div>
        <div className="container">
          <GradientText
            colors={neonColors}
            animationSpeed={6}
            className="text-[clamp(2.5rem,8vw,4.5rem)] mb-4 font-display font-semibold"
          >
            Get in Touch
          </GradientText>
          <p className="text-xl text-text-secondary font-light">
            We'd love to hear from you!
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 pb-[100px] bg-bg-secondary">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-start">
            {/* Contact Info */}
            <div className="animate-fade-in-left" id="contact-info">
              <span className="section-label">Contact</span>
              <GradientText
                colors={neonColors}
                animationSpeed={6}
                className="text-[2.5rem] mb-4 font-display font-semibold"
              >
                Let's Connect!
              </GradientText>
              <p className="text-text-secondary text-lg leading-relaxed mb-10">
                Got questions about our menu, want to book us for an event, or
                just want to say hello? We're all ears!
              </p>

              {/* Contact Methods */}
              <div className="flex flex-col gap-5 mb-12">
                <div
                  className="animate-fade-in-left contact-method flex gap-5 items-center p-5 bg-bg-card border border-border-default rounded-xl transition-all duration-300 hover:border-accent-purple hover:translate-x-2"
                  style={{ transitionDelay: "0.1s" }}
                >
                  <div className="text-[1.8rem] w-14 h-14 flex items-center justify-center bg-bg-elevated rounded-xl flex-shrink-0">
                    📧
                  </div>
                  <div>
                    <h3 className="font-body font-semibold text-text-primary mb-1">
                      Email Us
                    </h3>
                    <a
                      href="mailto:RetroGrillFoodTruck@gmail.com"
                      className="text-text-secondary text-[0.95rem] hover:text-accent-blue transition-colors"
                    >
                      RetroGrillFoodTruck@gmail.com
                    </a>
                  </div>
                </div>
                <div
                  className="animate-fade-in-left contact-method flex gap-5 items-center p-5 bg-bg-card border border-border-default rounded-xl transition-all duration-300 hover:border-accent-purple hover:translate-x-2"
                  style={{ transitionDelay: "0.2s" }}
                >
                  <div className="text-[1.8rem] w-14 h-14 flex items-center justify-center bg-bg-elevated rounded-xl flex-shrink-0">
                    📱
                  </div>
                  <div>
                    <h3 className="font-body font-semibold text-text-primary mb-1">
                      Call or Text
                    </h3>
                    <a
                      href="tel:+14436872020"
                      className="text-text-secondary text-[0.95rem] hover:text-accent-blue transition-colors"
                    >
                      + 1 (443)-687-2020
                    </a>
                  </div>
                </div>
              </div>

              {/* Social Section */}
              <div>
                <h3 className="font-body font-semibold text-text-primary mb-4">
                  Follow Us
                </h3>
                <div className="flex gap-4 flex-col sm:flex-row">
                  <a
                    href="https://www.facebook.com/retrogrillec"
                    target="_blank"
                    rel="noopener"
                    className="social-btn facebook justify-center"
                  >
                    <svg
                      className="w-5 h-5"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                    </svg>
                    <span>Facebook</span>
                  </a>
                  <a
                    href="https://www.instagram.com/retrogrillec"
                    target="_blank"
                    rel="noopener"
                    className="social-btn instagram justify-center"
                  >
                    <svg
                      className="w-5 h-5"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                    </svg>
                    <span>Instagram</span>
                  </a>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div
              className="animate-fade-in-right bg-bg-card border border-border-default rounded-[20px] p-12 md:p-12"
              id="contact-form-wrapper"
            >
              <form
                className="contact-form"
                id="contact-form"
                onSubmit={handleSubmit}
              >
                <h2 className="font-body text-2xl font-semibold text-text-primary mb-8">
                  Send Us a Message
                </h2>

                <div className="mb-6">
                  <label htmlFor="company" className="sr-only">
                    Company
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    tabIndex={-1}
                    autoComplete="off"
                    className="hidden"
                    aria-hidden="true"
                  />
                  <label
                    htmlFor="name"
                    className="block font-medium text-text-primary mb-2 text-[0.95rem]"
                  >
                    Your Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    placeholder="John Doe"
                    className="form-input"
                  />
                </div>

                <div className="mb-6">
                  <label
                    htmlFor="email"
                    className="block font-medium text-text-primary mb-2 text-[0.95rem]"
                  >
                    Your Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    placeholder="john@example.com"
                    className="form-input"
                  />
                </div>

                <div className="mb-6">
                  <label
                    htmlFor="subject"
                    className="block font-medium text-text-primary mb-2 text-[0.95rem]"
                  >
                    Subject
                  </label>
                  <select
                    id="subject"
                    name="subject"
                    required
                    value={selectedSubject}
                    onChange={(e) => setSelectedSubject(e.target.value)}
                    className="form-input"
                  >
                    <option value="catering">Catering & Private Events</option>
                    <option value="feedback">Feedback</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                {isCateringInquiry && (
                  <>
                    <div className="mb-6 animate-fade-in">
                      <label
                        htmlFor="event_date"
                        className="block font-medium text-text-primary mb-2 text-[0.95rem]"
                      >
                        Tentative Date
                      </label>
                      <input
                        type="text"
                        id="event_date"
                        name="event_date"
                        placeholder="e.g. June 13"
                        className="form-input"
                      />
                    </div>

                    <div className="mb-6 animate-fade-in">
                      <label
                        htmlFor="event_time"
                        className="block font-medium text-text-primary mb-2 text-[0.95rem]"
                      >
                        Tentative Time
                      </label>
                      <input
                        type="text"
                        id="event_time"
                        name="event_time"
                        placeholder="e.g. 4-8 PM"
                        className="form-input"
                      />
                    </div>

                    <div className="mb-6 animate-fade-in">
                      <label
                        htmlFor="guest_count"
                        className="block font-medium text-text-primary mb-2 text-[0.95rem]"
                      >
                        Approximate Guest Count
                      </label>
                      <input
                        type="text"
                        id="guest_count"
                        name="guest_count"
                        placeholder="Approximate guest count"
                        className="form-input"
                      />
                    </div>

                    <div className="mb-6 animate-fade-in">
                      <label
                        htmlFor="phone"
                        className="block font-medium text-text-primary mb-2 text-[0.95rem]"
                      >
                        Contact Phone Number
                      </label>
                      <input
                        type="text"
                        id="phone"
                        name="phone"
                        placeholder="(443) 555-1234"
                        className="form-input"
                      />
                    </div>
                  </>
                )}

                <div className="mb-6">
                  <label
                    htmlFor="message"
                    className="block font-medium text-text-primary mb-2 text-[0.95rem]"
                  >
                    Your Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={5}
                    required
                    placeholder="Tell us what's on your mind..."
                    className="form-input resize-y min-h-[130px]"
                  ></textarea>
                </div>

                <button
                  type="submit"
                  disabled={submitState === "submitting"}
                  className={`btn w-full ${
                    submitState === "success"
                      ? "bg-green-500 hover:bg-green-500"
                      : submitState === "error"
                        ? "bg-red-500 hover:bg-red-500"
                        : "btn-primary"
                  } ${submitState === "submitting" ? "cursor-not-allowed opacity-80" : ""}`}
                  id="submit-btn"
                >
                  <span className="btn-text">
                    {submitState === "submitting"
                      ? "Sending..."
                      : submitState === "success"
                        ? "✓ Message Sent"
                        : submitState === "error"
                          ? "Try Again"
                          : "Send Message"}
                  </span>
                </button>
                {submitMessage && (
                  <p className="mt-4 text-sm text-text-secondary">
                    {submitMessage}
                  </p>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
